import {useEffect, useRef, useState} from 'react';
import IconNext from '../../../icons/icon-next/icon-next';
import {ButtonIcon} from '../../../inputs/button-icon/button-icon';
import './grid-line.scss';
import useScrollComponent from '../../../../hooks/scoll-hook/scroll-hook-component';
import { ITooltipProps, Tooltip } from '../../../tooltip/tooltip';
import { IBaseGridStyle, useGridStyleHook } from '../../hooks/grid-style-hook';
import { useResizeDetector } from 'react-resize-detector';


export enum BackgroundColor {
    DEFAULT = 'var(--background-grey)',
    LIGHT = 'var(--color-neutral-1-white)',
    NEUTRAL6 = 'var(--color-neutral-6)',
}

export enum SIZE_VARIANT {
    DEFAULT = 'grid-line-size-default',
    SMALL = 'grid-line-size-small',
}

interface IGridLineTooltipProps extends Omit<ITooltipProps, 'children'> {}

interface Props extends IBaseGridStyle {
    isExpanded?: boolean;
    isExpandable?: boolean;
    isExpandDisabled?: boolean;
    expandableContent?: React.ReactNode;
    expandCallback?: (expanded: boolean) => void,
    backgroudColor?: BackgroundColor;
    withBorder?: boolean;
    withoutSidePadding?: boolean;
    disabled?: boolean;
    disabledTooltip?: IGridLineTooltipProps;
    onClick?: () => void;
    sizeVariant?: SIZE_VARIANT;
}

const GridLine: React.FC<Props> = ({
                                       settings = {columns: []},
                                       columns = [],
                                       isExpanded = false,
                                       isExpandable = false,
                                       isExpandDisabled = false,
                                       expandableContent,
                                       expandCallback,
                                       backgroudColor = BackgroundColor.DEFAULT,
                                       onClick,
                                       withBorder,
                                       withoutSidePadding,
                                       disabled,
                                       disabledTooltip,
                                       sizeVariant = SIZE_VARIANT.DEFAULT,
                                   }) => {

    const gridStyle = useGridStyleHook(settings, columns);

    const expandContentRef = useRef<HTMLDivElement>(null);
    const [_isExpanded, setIsExpanded] = useState(isExpanded);
    const [height, setHeight] = useState({ height: isExpanded ? expandContentRef.current?.scrollHeight : '0' });
    const { elemRef, scrollIntoView } = useScrollComponent<HTMLDivElement>();

    useResizeDetector<HTMLDivElement>({
        targetRef: expandContentRef,
        onResize: (size) => {
            if (expandContentRef.current && _isExpanded) {
                setHeight({height: size.height ?? 0});
            }
        },
    });

    useEffect(() => {
        setIsExpanded(isExpanded);
        setHeight({
            height: `${isExpanded ? expandContentRef.current?.scrollHeight : 0}px`
        });
    }, [isExpanded]);

    const toggleExpand = () => {
        if (disabled) {
            return;
        }

        if (!_isExpanded) {
            scrollIntoView();
        }

        setIsExpanded(!_isExpanded);
        if (expandCallback) {
            expandCallback(!_isExpanded);
        }
        setHeight({
            height: `${!_isExpanded ? expandContentRef.current?.scrollHeight: 0}px`
        });
    };

    const backgroundStyle = {
        backgroundColor: backgroudColor
    };

    const clickableClass = onClick ? ' clickable' : '';
    const withBorderClass = (withBorder || withBorder === undefined) ? ' grid-line-bordered' : '';
    const withoutSidePaddingsClass = withoutSidePadding ? ' grid-line-without-side-paddings' : '';
    const sizeClass = sizeVariant? ` ${sizeVariant}` : '';
    const lineDisabled = disabled ? ' grid-line-disabled' : '';

    const GridLineComponent = (
        <div ref={elemRef} className={`grid-line-wrapper${withBorderClass}${lineDisabled}${sizeClass}`}
             style={{...backgroundStyle}}
        >
            <div className={`grid-line-visible${clickableClass}${withoutSidePaddingsClass}`} onClick={() => {
                isExpandable && toggleExpand();
                onClick && onClick()
            }}>
                <div className={`grid-line`} style={{...gridStyle}}>
                    {columns.map((c, i) => <div key={i}>{c}</div>)}
                </div>

                {isExpandable &&
                    <div className={`expand-icon${_isExpanded ? ' expanded' : ' collapsed'} ${isExpandDisabled ? ' disabled' : ''}`}>
                        <ButtonIcon Component={IconNext} onClick={toggleExpand}/>
                    </div>
                }
            </div>
            
            <div className="grid-line-collapsable" style={height}>
                <div ref={expandContentRef}>
                    {expandableContent && <div className={`expand-content${_isExpanded ? ' expanded' : ''}`}>{expandableContent}</div>}
                </div>
            </div>

        </div>
    )

    return (disabled && disabledTooltip) 
        ? <div className="has-disabled-tooltip"><Tooltip {...disabledTooltip}>{GridLineComponent}</Tooltip></div>
        : GridLineComponent;
}

export default GridLine;
